import { createRouter, createWebHistory } from 'vue-router'
import { hasAdminSession } from '@/services/api'

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 }
  },
  routes: [
    {
      path: '/',
      name: 'Dashboard',
      component: () => import('@/views/Pages/DashboardPage.vue'),
      meta: {
        title: 'Dashboard',
      },
    },
    {
      path: '/users',
      name: 'Users',
      component: () => import('@/views/Pages/UsersPage.vue'),
      meta: {
        title: 'Users',
      },
    },
    {
      path: '/admin/users/:id/kyc',
      name: 'UserKyc',
      component: () => import('@/components/users/UserKycPage.vue'),
      meta: {
        title: 'User KYC',
      },
    },
    {
      path: '/admin/users/:id/inventory',
      name: 'UserInventory',
      component: () => import('@/components/inventory/UserInventoryPage.vue'),
      meta: {
        title: 'User Inventory',
      },
    },
    {
      path: '/admin/users/:id/active',
      name: 'UserActiveDevices',
      component: () => import('@/components/inventory/UserActiveDevicesPage.vue'),
      meta: {
        title: 'Active Devices',
      },
    },
    {
      path: '/admin/users/:id/wallet-transactions',
      name: 'UserWalletTransactions',
      component: () => import('@/components/users/UserWalletTransactionsPage.vue'),
      meta: {
        title: 'User Wallet Transactions',
      },
    },
    {
      path: '/admin/users/:id/transactions',
      name: 'UserTransactions',
      component: () => import('@/components/users/UserTransactionsPage.vue'),
      meta: {
        title: 'Transactions',
      },
    },
    {
      path: '/admin/users/:id/tickets',
      name: 'UserTickets',
      component: () => import('@/components/users/UserTicketsPage.vue'),
      meta: {
        title: 'User Tickets',
      },
    },
    {
      path: '/admin/users/:id/limits',
      name: 'UserLimits',
      component: () => import('@/components/users/UserLimitsPage.vue'),
      meta: {
        title: 'User Limits',
      },
    },
    {
      path: '/admin/users/:id/notes',
      name: 'UserNotes',
      component: () => import('@/components/users/UserNotesPage.vue'),
      meta: {
        title: 'User Notes',
      },
    },
    {
      path: '/admin/users/:id/ledgers',
      name: 'UserLedgers',
      component: () => import('@/components/users/UserLedgersPage.vue'),
      meta: {
        title: 'Ledgers',
      },
    },

    {
      path: '/transfers',
      name: 'Transfers',
      component: () => import('@/views/Pages/TransfersPage.vue'),
      meta: {
        title: 'Transfers',
      },
    },
    {
      path: '/withdrawals',
      name: 'Withdrawals',
      component: () => import('@/views/Pages/UserWithdrawalsPage.vue'),
      meta: {
        title: 'Withdrawals',
      },
    },
    {
      path: '/deposits',
      name: 'Deposits',
      component: () => import('@/views/Pages/DepositsPage.vue'),
      meta: {
        title: 'Deposits',
      },
    },
    {
      path: '/refunds',
      name: 'Refunds',
      component: () => import('@/views/Pages/RefundsPage.vue'),
      meta: {
        title: 'Refunds',
      },
    },

    {
      path: '/support/tickets',
      name: 'SupportTickets',
      component: () => import('@/views/Pages/SupportPage.vue'),
      meta: {
        title: 'Support Tickets',
      },
    },
    {
      path: '/support/knowledge-base',
      name: 'SupportKnowledgeBase',
      component: () => import('@/components/support/FaqsPage.vue'),
      meta: {
        title: 'Knowledge Base',
      },
    },
    {
      path: '/support/config',
      name: 'SupportConfig',
      component: () => import('@/views/Pages/SupportConfigPage.vue'),
      meta: {
        title: 'Support Config',
      },
    },

    {
      path: '/beneficiaries',
      name: 'Beneficiaries',
      component: () => import('@/views/Pages/BeneficiariesPage.vue'),
      meta: {
        title: 'Beneficiaries',
      },
    },
    {
      path: '/compliance/control-center',
      name: 'ComplianceControlCenter',
      component: () => import('@/views/Pages/ComplianceControlCenterPage.vue'),
      meta: {
        title: 'Compliance Control Center',
      },
    },
    {
      path: '/compliance/reviews',
      name: 'ComplianceReviews',
      component: () => import('@/views/Pages/ComplianceReviewsPage.vue'),
      meta: {
        title: 'Compliance Reviews',
      },
    },
    {
      path: '/compliance/reconciliations',
      name: 'ComplianceReconciliations',
      component: () => import('@/views/Pages/ComplianceReconciliationsPage.vue'),
      meta: {
        title: 'Wallet Reconciliations',
      },
    },
    {
      path: '/compliance/kyc',
      name: 'ComplianceKyc',
      component: () => import('@/views/Pages/KycSubmissionsPage.vue'),
      meta: {
        title: 'KYC Submissions',
      },
    },
    {
      path: '/security/freezes',
      name: 'SecurityFreezes',
      component: () => import('@/views/Pages/SecurityControlPage.vue'),
      meta: {
        title: 'Freezes & Security Controls',
      },
    },
    {
      path: '/security/activity',
      name: 'SecurityActivity',
      component: () => import('@/views/Pages/SecurityLogsPage.vue'),
      meta: {
        title: 'Security Activity',
      },
    },
    {
      path: '/audit-log',
      name: 'AuditLog',
      component: () => import('@/views/Pages/AuditLogsPage.vue'),
      meta: {
        title: 'Audit Log',
      },
    },

    {
      path: '/wallet-management/platform-wallets',
      name: 'PlatformWallets',
      component: () => import('@/views/Pages/WalletAddressManager.vue'),
      meta: {
        title: 'Platform Wallets',
      },
    },
    {
      path: '/wallet-management/sweeps',
      name: 'WalletSweeps',
      component: () => import('@/views/Pages/WalletSweepsPage.vue'),
      meta: {
        title: 'Sweep Transactions',
      },
    },
    {
      path: '/wallet-management/reconciliation',
      name: 'WalletReconciliation',
      component: () => import('@/views/Pages/WalletReconciliationPage.vue'),
      meta: {
        title: 'Wallet Reconciliation',
      },
    },
    {
      path: '/wallet-management/alerts',
      name: 'WalletAlerts',
      component: () => import('@/views/Pages/WalletAlertsPage.vue'),
      meta: {
        title: 'Wallet Alerts',
      },
    },
    {
      path: '/treasury',
      name: 'TreasuryOverview',
      component: () => import('@/views/Pages/TreasuryOverviewPage.vue'),
      meta: {
        title: 'Treasury',
      },
    },
    {
      path: '/rates',
      name: 'RatesConsole',
      component: () => import('@/views/Pages/RatesConsolePage.vue'),
      meta: {
        title: 'Rates',
      },
    },

    {
      path: '/cms/content',
      name: 'CmsContent',
      component: () => import('@/views/Pages/CmsContentPage.vue'),
      meta: {
        title: 'CMS Content',
      },
    },
    {
      path: '/cms/notifications',
      name: 'NotificationTemplates',
      component: () => import('@/views/Pages/NotificationTemplatesPage.vue'),
      meta: {
        title: 'Notification Templates',
      },
    },

    {
      path: '/referrals',
      name: 'Referrals',
      component: () => import('@/views/Pages/ReferralsPage.vue'),
      meta: {
        title: 'Referrals',
      },
    },
    {
      path: '/privacy',
      name: 'PrivacyGovernance',
      component: () => import('@/views/Pages/PrivacyGovernancePage.vue'),
      meta: {
        title: 'Privacy & Data Rights',
      },
    },
    {
      path: '/legal-documents',
      name: 'LegalDocuments',
      component: () => import('@/views/Pages/LegalDocumentsPage.vue'),
      meta: {
        title: 'Legal Documents',
      },
    },
    {
      path: '/onboarding-config',
      name: 'OnboardingConfig',
      component: () => import('@/views/Pages/OnboardingConfigPage.vue'),
      meta: {
        title: 'Onboarding Configuration',
      },
    },
    {
      path: '/runtime-config',
      name: 'RuntimeConfig',
      component: () => import('@/views/Pages/SystemConstantsPage.vue'),
      meta: {
        title: 'Runtime Configuration',
      },
    },
    {
      path: '/feature-flags',
      name: 'FeatureFlags',
      component: () => import('@/views/Pages/FeatureFlagsPage.vue'),
      meta: {
        title: 'Feature Flags',
      },
    },

    {
      path: '/reports/operations',
      name: 'OperationalReports',
      component: () => import('@/views/Pages/ReportingAnalyticsPage.vue'),
      meta: {
        title: 'Operational Reports',
      },
    },
    {
      path: '/reports/financial',
      name: 'FinancialReports',
      component: () => import('@/views/Pages/FinancialReportsPage.vue'),
      meta: {
        title: 'Financial Reports',
      },
    },

    {
      path: '/profile',
      name: 'Profile',
      component: () => import('@/views/Others/UserProfile.vue'),
      meta: {
        title: 'Profile',
      },
    },
    {
      path: '/admin-experience',
      name: 'AdminExperience',
      component: () => import('@/views/Pages/AdminExperiencePage.vue'),
      meta: {
        title: 'Admin Experience',
      },
    },
    {
      path: '/login',
      name: 'Signin',
      component: () => import('@/views/Auth/SignIn.vue'),
      meta: {
        title: 'Sign In',
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404 Error',
      component: () => import('@/views/Errors/FourZeroFour.vue'),
      meta: {
        title: '404 Error',
      },
    },
  ],
})

router.beforeEach((to, from, next) => {
  document.title = `TransferPier - ${to.meta.title}`

  const publicPages = ['/login']
  const authRequired = !publicPages.includes(to.path)
  const hasSession = hasAdminSession()

  if (authRequired && !hasSession) {
    return next({ path: '/login' })
  }

  if (hasSession && publicPages.includes(to.path)) {
    return next({ name: 'Dashboard' })
  }

  next()
})

export default router
